import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getSubItemLink = (subItem) => {
    switch(subItem) {
      case 'Testimonials':
        return '/testimonials';
      case 'Faculty and Staff':
        return '/faculty';
      case 'Scholarship Programs':
        return '/scholarships';
      case 'Classes & Programs':
        return '/classes';

      case 'NBT Photo Gallery':
        return '/photo-gallery';
      case 'NBT Repertoire and Reviews':
        return '/repertoire-reviews';
      case 'Meet the Board':
        return '/meet-the-board';
      case 'Support NB':
        return '/donate';
      case 'NBT Rehearsal Schedule':
        return '/rehearsal-schedule';
      case 'NBT Company and Performance Casting Requirements':
        return '/casting-requirements';
      default:
        return '#';
    }
  };

  const navigationItems = [
    {
      title: 'NB Theater',
      submenu: [
        'Testimonials',
        'NBT Photo Gallery',
        'NBT Repertoire and Reviews',
        'Meet the Board',
        'Scholarship Programs',
        'Support NB'
      ]
    },
    {
      title: 'NB Academy',
      submenu: [
        'NBT Rehearsal Schedule',
        'Classes & Programs',
        'Faculty and Staff',
        'NBT Company and Performance Casting Requirements',
        'Scholarship Programs'
      ]
    },
    { title: 'News Room' },
    { title: 'Contact Us' },
    { title: 'Donate' }
  ];

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-elegant' : 'bg-white shadow-lg'
    }`}>
      <div className="container-custom">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200">
            <div className="w-12 h-12 bg-elegant-gradient rounded-xl flex items-center justify-center">
              <span className="text-white font-elegant font-bold text-xl">NB</span>
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-elegant font-bold gradient-text">
                Northwest Ballet
              </h1>
              <p className="text-xs text-gray-500 hidden sm:block">Academy & Theater</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item, index) => (
              <div key={index} className="relative group">
                {item.title === 'Contact Us' ? (
                  <Link
                    to="/contact-us"
                    className={`nav-link py-2 flex items-center space-x-1 ${
                      location.pathname === '/contact-us' ? 'text-ballet-burgundy font-semibold' : ''
                    }`}
                  >
                    <span>{item.title}</span>
                  </Link>
                ) : item.title === 'News Room' ? (
                  <Link
                    to="/news-room"
                    className={`nav-link py-2 flex items-center space-x-1 ${
                      location.pathname === '/news-room' ? 'text-ballet-burgundy font-semibold' : ''
                    }`}
                  >
                    <span>{item.title}</span>
                  </Link>
                ) : item.title === 'NB Academy' ? (
                  <Link
                    to="/academy"
                    className={`nav-link py-2 flex items-center space-x-1 ${
                      location.pathname === '/academy' ? 'text-ballet-burgundy font-semibold' : ''
                    }`}
                  >
                    <span>{item.title}</span>
                    {item.submenu && (
                      <svg className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180"
                           fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </Link>
                ) : item.title === 'NB Theater' ? (
                  <Link
                    to="/theater"
                    className={`nav-link py-2 flex items-center space-x-1 ${
                      location.pathname === '/theater' ? 'text-ballet-burgundy font-semibold' : ''
                    }`}
                  >
                    <span>{item.title}</span>
                    {item.submenu && (
                      <svg className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180"
                           fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </Link>
                ) : item.title === 'Donate' ? (
                  <Link
                    to="/donate"
                    className={`nav-link py-2 flex items-center space-x-1 ${
                      location.pathname === '/donate' ? 'text-ballet-burgundy font-semibold' : ''
                    }`}
                  >
                    <span>{item.title}</span>
                  </Link>
                ) : (
                  <button className="nav-link py-2 flex items-center space-x-1">
                    <span>{item.title}</span>
                    {item.submenu && (
                      <svg className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180"
                           fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>
                )}

                {/* Enhanced Dropdown Menu */}
                {item.submenu && (
                  <div className="absolute left-0 mt-2 w-72 glass rounded-2xl shadow-elegant opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 transform translate-y-2 group-hover:translate-y-0">
                    <div className="p-2">
                      {item.submenu.map((subItem, subIndex) => {
                        const link = getSubItemLink(subItem);
                        return link === '#' ? (
                          <a
                            key={subIndex}
                            href="#"
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-ballet-pink/50 hover:text-ballet-burgundy rounded-xl transition-all duration-200 transform hover:translate-x-1"
                          >
                            {subItem}
                          </a>
                        ) : (
                          <Link
                            key={subIndex}
                            to={link}
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-ballet-pink/50 hover:text-ballet-burgundy rounded-xl transition-all duration-200 transform hover:translate-x-1"
                          >
                            {subItem}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              className="p-2 text-gray-600 hover:text-ballet-burgundy transition-colors duration-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <svg className={`w-6 h-6 transition-transform duration-300 ${isMenuOpen ? 'rotate-90' : ''}`}
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Enhanced Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white/95 backdrop-blur-md">
            <div className="py-6 space-y-4">
              {navigationItems.map((item, index) => (
                <div key={index} className="px-4">
                  {item.title === 'Contact Us' ? (
                    <Link
                      to="/contact-us"
                      className={`w-full text-left font-medium text-gray-800 hover:text-ballet-burgundy py-3 flex items-center justify-between transition-colors duration-200 ${
                        location.pathname === '/contact-us' ? 'text-ballet-burgundy font-semibold' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <span>{item.title}</span>
                    </Link>
                  ) : item.title === 'News Room' ? (
                    <Link
                      to="/news-room"
                      className={`w-full text-left font-medium text-gray-800 hover:text-ballet-burgundy py-3 flex items-center justify-between transition-colors duration-200 ${
                        location.pathname === '/news-room' ? 'text-ballet-burgundy font-semibold' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <span>{item.title}</span>
                    </Link>
                  ) : item.title === 'NB Academy' ? (
                    <Link
                      to="/academy"
                      className={`w-full text-left font-medium text-gray-800 hover:text-ballet-burgundy py-3 flex items-center justify-between transition-colors duration-200 ${
                        location.pathname === '/academy' ? 'text-ballet-burgundy font-semibold' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <span>{item.title}</span>
                      {item.submenu && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </Link>
                  ) : item.title === 'NB Theater' ? (
                    <Link
                      to="/theater"
                      className={`w-full text-left font-medium text-gray-800 hover:text-ballet-burgundy py-3 flex items-center justify-between transition-colors duration-200 ${
                        location.pathname === '/theater' ? 'text-ballet-burgundy font-semibold' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <span>{item.title}</span>
                      {item.submenu && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </Link>
                  ) : item.title === 'Donate' ? (
                    <Link
                      to="/donate"
                      className={`w-full text-left font-medium text-gray-800 hover:text-ballet-burgundy py-3 flex items-center justify-between transition-colors duration-200 ${
                        location.pathname === '/donate' ? 'text-ballet-burgundy font-semibold' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <span>{item.title}</span>
                    </Link>
                  ) : (
                    <button className="w-full text-left font-medium text-gray-800 hover:text-ballet-burgundy py-3 flex items-center justify-between transition-colors duration-200">
                      <span>{item.title}</span>
                      {item.submenu && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                  )}
                  {item.submenu && (
                    <div className="ml-4 mt-2 space-y-2 border-l-2 border-ballet-pink pl-4">
                      {item.submenu.map((subItem, subIndex) => {
                        const link = getSubItemLink(subItem);
                        return link === '#' ? (
                          <a
                            key={subIndex}
                            href="#"
                            className="block text-sm text-gray-600 hover:text-ballet-burgundy py-2 transition-colors duration-200"
                          >
                            {subItem}
                          </a>
                        ) : (
                          <Link
                            key={subIndex}
                            to={link}
                            className="block text-sm text-gray-600 hover:text-ballet-burgundy py-2 transition-colors duration-200"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {subItem}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;